import { FormItem } from 'vnetwork-ui'
import SubHeaders from '@/components/Headers/SubHeaders'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Dayjs } from 'dayjs'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Typogaphy } from 'vnetwork-ui'
import {
    EMPLOYEE_CONTRACT_TYPE,
    EMPLOYEE_SENIORITY_LEVEL,
    EMPLOYEE_EXTERNAL_TITLE,
} from '../../constants'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { getEmployeeInfo, updateEmployeeAdvanced } from '../../services'
import { Loading } from 'vnetwork-ui'
import { useEffect } from 'react'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface UpdateAdvancedFormValues {
    employee_day_off_left: number
    employee_start_date: Dayjs | null
    employee_end_date: Dayjs | null
    employee_contract_type: string
    employee_seniority_level: string
    employee_external_title: string
    employee_name: string
    // Trang này KHÔNG render 3 ô nguồn CV và KHÔNG đưa chúng vào defaultValues.
    // Khai optional để type khớp payload chung; bỏ trống nghĩa là backend giữ nguyên giá trị cũ.
    employee_cv_source?: string
    employee_cv_source_detail?: string
    employee_cv_cover_letter_id?: number
}

const schema = yup.object<UpdateAdvancedFormValues>().shape({
    employee_day_off_left: yup.number().min(0),
    employee_contract_type: yup.string().required(),
    employee_seniority_level: yup.string().required(),
    employee_name: yup.string().required(),
})

const UpdateAdvanced = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const { control, reset, handleSubmit } = useForm<UpdateAdvancedFormValues>({
        defaultValues: {
            employee_day_off_left: 0,
            employee_end_date: null,
            employee_start_date: null,
            employee_contract_type: EMPLOYEE_CONTRACT_TYPE(t)?.[0]?.value,
            employee_seniority_level: EMPLOYEE_SENIORITY_LEVEL?.[0]?.value,
            employee_external_title: '',
        },
        // @ts-ignore
        resolver: yupResolver(schema),
    })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EMPLOYEE_UPDATE_ADVANCED, id],
        queryFn: () => getEmployeeInfo(Number(id)),
        loadingComponent: (
            <div className="p-4">
                <Loading.CircularProgress width={20} height={20} />
            </div>
        ),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: updateEmployeeAdvanced,
        },
        {
            reactQueryKeys: QUERY_KEYS.EMPLOYEES,
            successMessage: 'update-success',
            errorMessage: 'error-occurred',
            onSuccess: () => {
                navigate(paths.employeesManage)
            },
        }
    )

    const handleUpdateAvanced: any = (values: UpdateAdvancedFormValues) =>
        mutateAsync({
            ...values,
            employee_id: Number(id),
        })

    useEffect(() => {
        reset({
            employee_day_off_left: data?.result?.employee_day_off_left,
            employee_end_date: data?.result?.employee_end_date,
            employee_start_date: data?.result?.employee_start_date,
            employee_contract_type: data?.result?.employee_contract_type,
            employee_seniority_level: data?.result?.employee_seniority_level,
            employee_external_title:
                data?.result?.employee_external_title || '',
            employee_name: data?.result?.employee_name,
        })
    }, [data?.result])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.employeesManage,
                        title: t('employee-manage'),
                    },
                    {
                        path: generatePathDatacentral(
                            paths.employeeUpdateAdvanced,
                            {
                                id,
                            }
                        ),
                        title: `#${id}`,
                    },
                    {
                        path: generatePathDatacentral(
                            paths.employeeUpdateAdvanced,
                            {
                                id,
                            }
                        ),
                        title: t('edit-advanced'),
                    },
                ]}
            />

            <form
                className="p-4 rounded-[8px] bg-white mt-2"
                onSubmit={handleSubmit(handleUpdateAvanced)}
            >
                <Typogaphy size="medium" className="font-semibold">
                    {t('edit-advanced')} - #{id} - {data?.result?.employee_name}
                </Typogaphy>

                <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mt-4">
                    <FormItem
                        control={control}
                        name="employee_name"
                        type="textfield"
                        placeholder={t('employee_name')}
                        label={t('employee_name')}
                    />

                    <FormItem
                        control={control}
                        name="employee_day_off_left"
                        type="number"
                        placeholder={t('employee_day_off_left')}
                        label={t('employee_day_off_left')}
                    />

                    <FormItem
                        control={control}
                        name="employee_contract_type"
                        type="select"
                        placeholder={t('employee_contract_type_placeholder')}
                        label={t('employee_contract_type')}
                        options={EMPLOYEE_CONTRACT_TYPE(t)}
                    />

                    <FormItem
                        control={control}
                        name="employee_seniority_level"
                        type="select"
                        placeholder={t('employee_seniority_level_placeholder')}
                        label={t('employee_seniority_level')}
                        options={EMPLOYEE_SENIORITY_LEVEL}
                    />

                    <FormItem
                        control={control}
                        name="employee_external_title"
                        type="select"
                        placeholder={t('employee_external_title')}
                        label={t('employee_external_title')}
                        options={EMPLOYEE_EXTERNAL_TITLE}
                    />

                    <FormItem
                        control={control}
                        name="employee_start_date"
                        type="datepicker"
                        placeholder={t('employee_start_date')}
                        label={t('employee_start_date')}
                    />

                    <FormItem
                        control={control}
                        name="employee_end_date"
                        type="datepicker"
                        placeholder={t('employee_end_date')}
                        label={t('employee_end_date')}
                        disabled
                    />
                </div>

                <div className="mt-4 flex justify-end">
                    <Button loading={isPending} disabled={isPending}>
                        {t('updated')}
                    </Button>
                </div>
            </form>
        </div>
    )
}

export default UpdateAdvanced
